import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Alert,
  Tab,
  Tabs,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Subscriptions as SubscriptionsIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import {
  blue,
  green,
  red,
  grey,
  amber,
  purple,
} from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

const PaymentDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getPaymentByIdApiCall } = useAdminContext();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        setLoading(true);
        const paymentData = await getPaymentByIdApiCall(id);
        if (paymentData) {
          setPayment(paymentData);
        }
      } catch (error) {
        console.error("Failed to fetch payment:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    history.push(ADMIN_ROUTES.payments);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewUser = () => {
    if (payment?.user_id) {
      history.push(ADMIN_ROUTES.userDetail.replace(":id", payment.user_id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "refunded":
        return "info";
      case "canceled":
        return "default";
      default:
        return "default";
    }
  };

  const getPaymentTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return <WalletIcon />;
      case "subscription_new":
      case "subscription_renewal":
      case "subscription_change":
        return <SubscriptionsIcon />;
      default:
        return <CreditCardIcon />;
    }
  };

  const getPaymentTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return "Wallet Funding";
      case "subscription_new":
        return "New Subscription";
      case "subscription_renewal":
        return "Subscription Renewal";
      case "subscription_change":
        return "Plan Change";
      default:
        return type || "Unknown";
    }
  };

  const getProviderInfo = (provider) => {
    switch (provider?.toLowerCase()) {
      case "stripe":
        return { name: "Stripe", icon: "💳", color: blue[500] };
      case "binance":
        return { name: "Binance", icon: "🪙", color: amber[600] };
      default:
        return { name: provider || "Unknown", icon: "💰", color: grey[500] };
    }
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "basic":
        return grey[600];
      case "standard":
        return blue[600];
      case "premium":
        return amber[600];
      case "elite":
        return red[600];
      default:
        return grey[600];
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!payment) {
    return (
      <AdminLayout>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
            Back to Payments
          </Button>
          <Typography variant="h6">Payment not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const providerInfo = getProviderInfo(payment.payment_provider);
  const isRefunded = payment.status === "refunded";
  const hasFailed = payment.status === "failed";
  const isPending = payment.status === "pending";
  const isCompleted = payment.status === "completed";

  return (
    <AdminLayout>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Back to Payments
        </Button>

        {/* Header Section */}
        <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Avatar
              sx={{
                bgcolor:
                  getStatusColor(payment.status) === "success"
                    ? green[500]
                    : getStatusColor(payment.status) === "error"
                    ? red[500]
                    : getStatusColor(payment.status) === "warning"
                    ? amber[500]
                    : blue[500],
                width: 64,
                height: 64,
                boxShadow: 3,
              }}
            >
              {getPaymentTypeIcon(payment.payment_type)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Details
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Chip
                  label={payment.status?.toUpperCase() || "UNKNOWN"}
                  size="small"
                  color={getStatusColor(payment.status)}
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  icon={getPaymentTypeIcon(payment.payment_type)}
                  label={getPaymentTypeLabel(payment.payment_type)}
                  size="small"
                  variant="outlined"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace", ml: 1 }}
                >
                  ID: {payment.id}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: green[600] }}>
                {formatCurrency(payment.amount, payment.currency)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(payment.created_at)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Status Alerts */}
        {hasFailed && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Payment Failed:</strong> This payment was unsuccessful and needs attention.
            </Typography>
          </Alert>
        )}
        {isRefunded && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Payment Refunded:</strong> This payment has been refunded.
              {payment.refund_amount &&
                ` Amount: ${formatCurrency(payment.refund_amount, payment.currency)}`}
            </Typography>
          </Alert>
        )}
        {isPending && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Payment Pending:</strong> This payment is awaiting processing.
            </Typography>
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }} elevation={3}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tab label="Payment Info" />
            <Tab label="User Info" />
            <Tab label="Transaction Details" />
          </Tabs>

          {/* Tab 0: Payment Info */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Payment Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: green[600] }}>
                            {formatCurrency(payment.amount, payment.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Currency
                          </Typography>
                          <Typography variant="body1">{payment.currency || "USD"}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Payment Type
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              icon={getPaymentTypeIcon(payment.payment_type)}
                              label={getPaymentTypeLabel(payment.payment_type)}
                              size="small"
                              color="primary"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={payment.status?.toUpperCase() || "UNKNOWN"}
                              size="small"
                              color={getStatusColor(payment.status)}
                            />
                          </Box>
                        </Grid>
                        {payment.coins_purchased && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Coins Purchased
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: amber[600] }}>
                              🪙 {payment.coins_purchased?.toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Provider Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Payment Provider
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <span style={{ fontSize: "1.5rem" }}>{providerInfo.icon}</span>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: providerInfo.color }}>
                              {providerInfo.name}
                            </Typography>
                          </Box>
                        </Grid>
                        {payment.provider_payment_id && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Provider Payment ID
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}
                            >
                              {payment.provider_payment_id}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Payment ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}
                          >
                            {payment.id}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 1: User Info */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      User Information
                    </Typography>
                    {payment.user_id && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonIcon />}
                        onClick={handleViewUser}
                      >
                        View Full User Profile
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <Avatar
                          sx={{
                            bgcolor: getAccountTypeColor(payment.account_type),
                            width: 56,
                            height: 56,
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {payment.username || "Unknown User"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {payment.user_email}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Account Type
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={payment.account_type?.toUpperCase() || "BASIC"}
                              size="small"
                              sx={{
                                bgcolor: getAccountTypeColor(payment.account_type),
                                color: "white",
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            User ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", mt: 0.5 }}>
                            {payment.user_id}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Tab 2: Transaction Details */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Transaction Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Created At
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {formatDate(payment.created_at)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Updated At
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {formatDate(payment.updated_at)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Currency
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {payment.currency || "USD"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Payment Method
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {payment.payment_method || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Admin Notes */}
                {payment.admin_notes && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Admin Notes
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Paper sx={{ p: 2, backgroundColor: grey[50] }}>
                          <Typography variant="body2">{payment.admin_notes}</Typography>
                        </Paper>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Refund Information */}
                {isRefunded && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: blue[200], bgcolor: blue[50] }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: blue[700] }}>
                          Refund Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          {payment.refund_amount && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Refund Amount
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                {formatCurrency(payment.refund_amount, payment.currency)}
                              </Typography>
                            </Grid>
                          )}
                          {payment.refund_reason && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Refund Reason
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {payment.refund_reason}
                              </Typography>
                            </Grid>
                          )}
                          {payment.refunded_at && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Refunded At
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {formatDate(payment.refunded_at)}
                              </Typography>
                            </Grid>
                          )}
                          {payment.refunded_by && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Refunded By
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {payment.refunded_by}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default PaymentDetailPage;
